package com.sddamico.draftmachine.util

import com.sddamico.draftmachine.models.QuietWindowInfo

/**
 * Client-side helpers for the draft's overnight pause. The backend resolves the pause *windows*
 * into absolute instants (`OnClockInfo.quietWindows`), so everything here is plain interval
 * arithmetic — no timezone rules are re-implemented in the browser. The one exception is
 * [formatInZone], which delegates to `Intl` for display only.
 */

/** Milliseconds of [windows] that fall inside `[fromMs, toMs)`. */
private fun quietMillisBetween(fromMs: Double, toMs: Double, windows: List<QuietWindowInfo>): Double {
  if (toMs <= fromMs) return 0.0
  return windows.sumOf { window ->
    val start = kotlin.js.Date(window.startIso).getTime()
    val end = kotlin.js.Date(window.endIso).getTime()
    if (start.isNaN() || end.isNaN()) 0.0
    else (minOf(end, toMs) - maxOf(start, fromMs)).coerceAtLeast(0.0)
  }
}

/**
 * Milliseconds of *pick time* left at [nowMs] — wall-clock time to [deadlineMs] with the overnight
 * pauses removed. This is the number the countdown shows, so a team that goes on the clock at
 * 11 PM with an 8-hour timer sees 8 hours, not the 14 the wall clock will actually take.
 */
fun activeMillisRemaining(nowMs: Double, deadlineMs: Double, windows: List<QuietWindowInfo>): Double =
  (deadlineMs - nowMs - quietMillisBetween(nowMs, deadlineMs, windows)).coerceAtLeast(0.0)

/** The end of the pause running at [nowMs], or null when the clock is currently burning. */
fun quietWindowEndAt(nowMs: Double, windows: List<QuietWindowInfo>): Double? = windows
  .firstNotNullOfOrNull { window ->
    val start = kotlin.js.Date(window.startIso).getTime()
    val end = kotlin.js.Date(window.endIso).getTime()
    if (!start.isNaN() && !end.isNaN() && nowMs >= start && nowMs < end) end else null
  }

/** Render an epoch-millis instant as a short "6:00 AM EDT" label in [zone] (an IANA id). */
fun formatInZone(millis: Double, zone: String): String {
  val options = js("{}")
  options.timeZone = zone
  options.hour = "numeric"
  options.minute = "2-digit"
  options.timeZoneName = "short"
  return runCatching {
    kotlin.js.Date(millis).asDynamic().toLocaleString("en-US", options) as String
  }.getOrElse { "" }
}

/** "6:00 AM" for a minutes-from-local-midnight setting value. */
fun formatMinuteOfDay(minute: Int): String {
  val normalized = ((minute % 1440) + 1440) % 1440
  val hours24 = normalized / 60
  val suffix = if (hours24 < 12) "AM" else "PM"
  val hour12 = when {
    hours24 == 0 -> 12
    hours24 > 12 -> hours24 - 12
    else -> hours24
  }
  return "$hour12:${(normalized % 60).toString().padStart(2, '0')} $suffix"
}

/** Minutes-from-midnight → the "HH:MM" value an `<input type="time">` expects. */
fun minuteOfDayToTimeValue(minute: Int): String {
  val normalized = ((minute % 1440) + 1440) % 1440
  return "${(normalized / 60).toString().padStart(2, '0')}:${(normalized % 60).toString().padStart(2, '0')}"
}

/** "HH:MM" from an `<input type="time">` → minutes from midnight, or null if unparseable. */
fun timeValueToMinuteOfDay(value: String): Int? {
  val parts = value.split(":")
  if (parts.size < 2) return null
  val hours = parts[0].toIntOrNull() ?: return null
  val minutes = parts[1].toIntOrNull() ?: return null
  if (hours !in 0..23 || minutes !in 0..59) return null
  return hours * 60 + minutes
}

/** The timezone options offered in draft settings, in the order leagues are most likely to want. */
val QUIET_HOURS_ZONES: List<Pair<String, String>> = listOf(
  "America/New_York" to "Eastern (ET)",
  "America/Chicago" to "Central (CT)",
  "America/Denver" to "Mountain (MT)",
  "America/Phoenix" to "Arizona (MST)",
  "America/Los_Angeles" to "Pacific (PT)",
  "America/Anchorage" to "Alaska (AKT)",
  "Pacific/Honolulu" to "Hawaii (HST)",
  "Europe/London" to "United Kingdom (GMT/BST)",
  "UTC" to "UTC",
)

/** Display label for a stored zone id, falling back to the raw id for anything off the list. */
fun zoneLabel(zone: String): String = QUIET_HOURS_ZONES.firstOrNull { it.first == zone }?.second ?: zone
